import { DatePicker, Form, Modal, Select, Switch, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import CustomButton from 'components/custom-button';
import { TYPE_DEPARTMENT } from 'constants/common';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';

interface ExportAllUsersModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  month: Dayjs;
  department?: string;
  approvedOnly?: boolean;
}

const DEPARTMENT_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: TYPE_DEPARTMENT.HR, label: 'Nhân sự (HR)' },
  { value: TYPE_DEPARTMENT.TECHNICAL, label: 'Kĩ thuật (Technical)' },
  { value: TYPE_DEPARTMENT.MAKEUP, label: 'Makeup' },
  { value: TYPE_DEPARTMENT.MC, label: 'MC' },
  { value: TYPE_DEPARTMENT.IDOL, label: 'Creator' },
  { value: TYPE_DEPARTMENT.CONTENT, label: 'Content' },
];

// Status code → friendly Vietnamese message (per docs/export-attendance-all-users.md §5.4)
function statusToMessage(status?: number): string | null {
  if (status === 400) return 'Tham số không hợp lệ. Kiểm tra lại tháng và phòng ban.';
  if (status === 401) return 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
  if (status === 403) return 'Bạn không có quyền xuất dữ liệu này (chỉ ADMIN).';
  if (status && status >= 500) return 'Lỗi server. Vui lòng thử lại sau.';
  return null;
}

// Axios interceptor unwraps to `error.response.data` which, for blob responses,
// is itself a Blob containing JSON. Parse it back to extract the message.
export async function extractErrorMessage(err: any): Promise<string> {
  const fallback = 'Xuất file thất bại!';
  if (!err) return fallback;
  if (err instanceof Blob) {
    try {
      const text = await err.text();
      const parsed = JSON.parse(text);
      const friendly = statusToMessage(parsed?.statusCode);
      if (friendly) return friendly;
      const msg = parsed?.message;
      return Array.isArray(msg) ? msg[0] : msg || fallback;
    } catch {
      return fallback;
    }
  }
  if (typeof err === 'object') {
    const friendly = statusToMessage(err.statusCode || err.status);
    if (friendly) return friendly;
    const msg = err.message;
    if (Array.isArray(msg)) return msg[0];
    if (typeof msg === 'string') return msg;
  }
  return fallback;
}

export function parseFilename(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** Đoạn bộ phận trong tên file tải về: mã IDOL hiển thị là Creator nên ghi 'creator'. */
export function departmentFileSlug(department: string): string {
  if (department === TYPE_DEPARTMENT.IDOL) return 'creator';
  return department.toLowerCase().replace(/_/g, '-');
}

const ExportAllUsersModal: React.FC<ExportAllUsersModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    form.resetFields();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const monthStr = dayjs(values.month).format('YYYY-MM');
    const params: Record<string, string | boolean> = { month: monthStr };
    if (values.department) params.department = values.department;
    if (values.approvedOnly) params.approvedOnly = true;

    setLoading(true);
    try {
      const response: any = await AxiosUtils.get(API.attendanceExportExcelAll, params, undefined, {
        responseType: 'blob',
        timeout: 60_000,
      });

      const fallbackName = `attendance-all-users-${monthStr}${
        values.department ? `-${departmentFileSlug(values.department)}` : ''
      }.xlsx`;
      const filename = parseFilename(response?.headers?.['content-disposition'], fallbackName);

      const blob = new Blob([response.data], {
        type:
          response?.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerDownload(blob, filename);

      notification.success({ message: 'Thành công', description: 'Đã tải file' });
      form.resetFields();
      onClose();
    } catch (err) {
      const description = await extractErrorMessage(err);
      notification.error({ message: 'Không thành công', description });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Xuất Excel chấm công — toàn bộ nhân viên"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={!loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          month: dayjs(),
          department: '',
          approvedOnly: false,
        }}
      >
        <Form.Item
          name="month"
          label="Tháng"
          rules={[{ required: true, message: 'Vui lòng chọn tháng' }]}
        >
          <DatePicker
            picker="month"
            allowClear={false}
            size="large"
            style={{ width: '100%' }}
            format="YYYY-MM"
          />
        </Form.Item>

        <Form.Item name="department" label="Phòng ban">
          <Select size="large" options={DEPARTMENT_OPTIONS} />
        </Form.Item>

        <Form.Item name="approvedOnly" label="Chỉ lấy bản ghi đã duyệt" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <CustomButton
            text="Hủy"
            bgColor="primary-grey"
            onClick={handleClose}
            disabled={loading}
            className="mr-10"
          />
          <CustomButton
            text={loading ? 'Đang xuất...' : 'Xuất Excel'}
            bgColor="primary-green"
            htmlType="submit"
            loading={loading}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportAllUsersModal;
